/**
 * Shared types for One Up Command Center.
 *
 * Frontend Task / Client / Website / TimeEntry shapes are normalized in the
 * Cloudflare Worker — the frontend never sees raw Airtable field IDs.
 *
 * Keep this file dependency-free so it can run in both Worker (V8 isolate)
 * and Vite (browser) contexts.
 */

// ---------- Semantic enums (stable, frontend-facing) ----------

/**
 * Semantic status used by the UI for filtering and queue logic.
 * The Worker maps real Airtable Status option strings to these values.
 *
 * Real Airtable Status options observed: "Closed". Mapping in
 * docs/AIRTABLE.md and the Worker's normalize.ts.
 */
export type SemanticStatus =
  | 'new'
  | 'open'
  | 'in_progress'
  | 'waiting_on_client'
  | 'review'
  | 'done'
  | 'unknown';

/**
 * Semantic priority. Real Airtable values seen lowercased ("normal");
 * normalization tolerates either case.
 */
export type SemanticPriority = 'urgent' | 'high' | 'normal' | 'low' | 'unknown';

/** Semantic bill status. Real Airtable values include "Not Processed". */
export type SemanticBillStatus =
  | 'not_processed'
  | 'ready_to_bill'
  | 'billed'
  | 'not_billable'
  | 'unknown';

export const COMMAND_CENTER_FLAG_DEFAULTS = [
  'On Fire',
  'Shortlist',
  'Send To Client',
  '<5m',
] as const;

// ---------- Core entities ----------

export interface ClientRef {
  id?: string;
  name?: string;
  /** Optional logo URL — first attachment thumbnail, if present. */
  logoUrl?: string;
  /** Whether this client should be treated as "internal/One Up". */
  isInternal?: boolean;
}

export interface WebsiteRef {
  id?: string;
  url?: string;
  friendlyName?: string;
}

export interface UserRef {
  id?: string;
  name?: string;
  email?: string;
}

/** Task shape consumed by the frontend. */
export interface Task {
  id: string;
  name: string;
  /** Raw Airtable Status string (preserved for round-trip writes). */
  status: string;
  /** Semantic status for UI filtering — derived in the Worker. */
  semanticStatus: SemanticStatus;
  priority?: string;
  semanticPriority: SemanticPriority;

  client?: ClientRef;
  website?: WebsiteRef;
  owner?: UserRef;

  targetDate?: string; // ISO yyyy-mm-dd
  startDate?: string; // ISO yyyy-mm-dd
  /** Airtable record creation timestamp. */
  createdAt?: string;
  /** Last time the Status field changed in Airtable. */
  statusLastModified?: string;
  /** Softr/client-facing URL from Airtable `Client Portal Link`. */
  clientPortalLink?: string;

  parentTaskId?: string;
  parentTaskName?: string;
  childTaskIds?: string[];
  /** Detail-only first-level children; not recursively enriched. */
  childTasks?: Task[];

  estimateMinutes?: number;
  trackedMinutes?: number;
  weekTrackedMinutes?: number;
  subtaskTrackedMinutes?: number;
  totalTrackedWithSubtasksMinutes?: number;

  billStatus?: string;
  semanticBillStatus: SemanticBillStatus;
  approvalStatus?: string;
  finalReview?: string;
  isBillable?: boolean;

  notes?: string; // markdown
  /** Optional structured AI overview (Airtable AI field). */
  overview?: string;
  /**
   * Whether the AI Overview is stale (i.e. the underlying inputs changed
   * since the last time Airtable regenerated it). Surfaces a small "stale"
   * pill in the drawer so Dakota knows to re-generate or trust-but-verify.
   */
  overviewIsStale?: boolean;

  /** The very next concrete step. Persisted to Airtable `Next Action`. */
  nextAction?: string;
  /** Acceptance criteria. Persisted to Airtable `Definition of Done`. */
  definitionOfDone?: string;
  /**
   * Open questions to surface to the client on the next status update.
   * Persisted to Airtable `Question for Clients`. The Send Update button
   * includes these in the email body when populated.
   */
  questionForClients?: string;
  /** Airtable Screenshots attachment URLs. */
  screenshotUrls?: string[];

  /** Editable Airtable multiselect used as Command Center tags. */
  commandCenterFlags: string[];

  flags: TaskFlag[];

  /** Linked time entry IDs (for further fetching). */
  timeEntryIds?: string[];
  /** Inline-loaded entries when the drawer requests them. */
  timeEntries?: TimeEntry[];

  /** Linked invoice IDs from Airtable. */
  invoiceIds?: string[];

  /** Source-of-truth Airtable raw fields if a caller wants a deeper peek. */
  raw?: Record<string, unknown>;
}

export interface TimeEntry {
  id: string;
  name: string;
  taskId?: string;
  taskName?: string;
  clientId?: string;
  clientName?: string;
  notes?: string;
  billable: boolean;
  status?: string;
  source?: string;
  entryDate?: string; // ISO yyyy-mm-dd
  startedAt?: string; // ISO datetime
  endedAt?: string; // ISO datetime
  durationMinutes: number;
  rate?: number;
  totalPrice?: number;
  readyToBill?: boolean;
}

export interface Client {
  id: string;
  name: string;
  clientRecordId?: string;
  /** Stable human-readable handle used by Alice mentions, without the leading @. */
  clientHandle?: string;
  /** Short voice/assistant code used to disambiguate clients in Alice. */
  voiceCode?: string;
  /** Comma-separated aliases from Airtable used for client search and mention matching. */
  clientAliases?: string;
  status?: string;
  hourlyRate?: number;
  stripePriceId?: string;
  timeTokenRate?: number;
  website?: string;
  notes?: string;
  logoUrl?: string;
  isInternal?: boolean;
  industryType?: string;
  industryTypes?: string[];
  /** "Direct Client", "Direct Sub Client", etc. */
  relationshipType?: string;
  relationshipTypes?: string[];
  businessPhoneNumber?: string;
  orgContactEmail?: string;
  businessFullAddress?: string;
  businessStreetAddress?: string;
  businessStreetAddress2?: string;
  businessCity?: string;
  businessState?: string;
  businessZip?: string;
  businessShortDescription?: string;
  servicesDescription?: string;
  serviceArea?: string;
  coreBusinessSummary?: string;
  frequentlyAskedQuestions?: string;
  timeZone?: string;
  placeId?: string;
  sharedDriveFolderId?: string;
  googleDriveFolderUrl?: string;
  driveEmbed?: string;
  driveEmbedGrid?: string;
  contactIds?: string[];
  contactNames?: string[];
  projectIds?: string[];
  agentIds?: string[];
  quickNoteSubmissionIds?: string[];
  parentOrganizationId?: string;
  parentOrganizationName?: string;
  childOrganizationIds?: string[];
  webmasterIds?: string[];
  webmasterEmails?: string[];
  billingContactName?: string;
  designatedBillingContactId?: string;
  allBillingEmails?: string[];
  totalMinutesBillable?: number;
  totalMinutesTracked?: number;
  openSupportCount?: number;
  stripeCustomerId?: string;
  stripeCustomerUrl?: string;
  voiceAgentLicenses?: number;
  /** Linked website record IDs. */
  websiteIds?: string[];
  subscriptionIds?: string[];
  invoiceIds?: string[];
  proposalIds?: string[];
  timeEntryIds?: string[];
  walletAiVoiceIds?: string[];
  walletIds?: string[];
  paymentIds?: string[];
  leadIds?: string[];
  usdBalanceTimeTokens?: number;
  totalCurrentTimeFromAiVoice?: number;
  createdAt?: string;
  lastModifiedAt?: string;
}

export interface Website {
  id: string;
  websiteId?: string;
  url: string;
  friendlyName?: string;
  status?: string;
  type?: string;
  hostingPlatform?: string;
  hostedBy?: string;
  hostingPrice?: number;
  subscriptionIds?: string[];
  notes?: string;
  organizationId?: string;
  organizationName?: string;
  businessName?: string;
  logoUrl?: string;
  screenshotUrl?: string;
  desktopScreenUrl?: string;
  wpAdminUrl?: string;
  lastModifiedAt?: string;
  lastModifiedBy?: string;
  lastUser?: string;
}

export interface User {
  id: string;
  userId?: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  title?: string;
  profileImageUrl?: string;
  lastSeenAt?: string;
  status?: string;
  organizationStatuses?: string[];
  userTypes?: string[];
  clientIds?: string[];
  clientRecordIds?: string[];
  clientLogoUrl?: string;
  sharedDriveFolderId?: string;
  organizationWebmasterLinkIds?: string[];
  leadIds?: string[];
  quickNotesWebmasterIds?: string[];
  agentLicense?: string;
  createdAt?: string;
  lastModifiedAt?: string;
}

export type LeadStatus =
  | 'New Lead'
  | 'Spotted Opportunity'
  | 'In Progress'
  | 'Follow Up Later'
  | 'Signed!'
  | 'Archived';

export type ProposalStatus = 'Draft' | 'Submitted' | 'Approved' | 'Rejected' | 'Invoiced';

export type ProposalVisibility = 'Draft' | 'Admin Only' | 'Client Visible' | 'Archived';

export type ProposalType =
  | 'Website Build'
  | 'Webmaster Package'
  | 'Local SEO'
  | 'AI Voice Agent'
  | 'Automation'
  | 'Marketing Retainer'
  | 'Technical Estimate'
  | 'Custom';

export interface ProposalAttachment {
  id?: string;
  url: string;
  filename?: string;
  type?: string;
}

export interface Lead {
  id: string;
  projectName: string;
  fullName?: string;
  status?: LeadStatus | string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  businessWebsite?: string;
  leadNotes?: string;
  businessRelationTitle?: string;
  lastModifiedAt?: string;
  organizationIds?: string[];
  contactLinkIds?: string[];
  businessName?: string;
  businessLogo?: ProposalAttachment[];
  businessLogoUrl?: string;
  businessPhone?: string;
  proposalAttachments?: ProposalAttachment[];
  proposalSummary?: string;
  googleCalendarCreateMeetingUrl?: string;
  billProposalIds?: string[];
  createdAt?: string;
  raw?: Record<string, unknown>;
}

export type LeadInput = Partial<
  Pick<
    Lead,
    | 'projectName'
    | 'status'
    | 'firstName'
    | 'lastName'
    | 'phone'
    | 'email'
    | 'businessWebsite'
    | 'leadNotes'
    | 'businessRelationTitle'
    | 'organizationIds'
    | 'contactLinkIds'
    | 'businessName'
    | 'businessLogo'
    | 'businessPhone'
    | 'proposalAttachments'
    | 'billProposalIds'
  >
>;

export interface Proposal {
  id: string;
  proposalName: string;
  proposalType?: ProposalType;
  status?: ProposalStatus;
  portalVisibility?: ProposalVisibility;
  proposalAmount?: number;
  depositAmount?: number;
  proposalDate?: string;
  validUntil?: string;
  organizationIds?: string[];
  organizationLabel?: string;
  leadIds?: string[];
  leadLabel?: string;
  linkedInvoiceIds?: string[];
  servicesDescription?: string;
  scopeSummary?: string;
  deliverables?: string;
  timeline?: string;
  paymentTerms?: string;
  clientFacingNextStep?: string;
  internalNextAction?: string;
  notes?: string;
  generatedDocUrl?: string;
  generatedPdfUrl?: string;
  proposalDocument?: ProposalAttachment[];
  createdByUser?: string;
  approvalDate?: string;
  acceptedAt?: string;
  createdAt?: string;
  raw?: Record<string, unknown>;
}

export interface ProposalReadiness {
  score: number;
  missing: string[];
  warnings: string[];
}

export interface ActivityLogEntry {
  id: string;
  createdAt: string;
  actorId?: string;
  actorName?: string;
  entityType: 'task' | 'time_entry' | 'client' | 'website' | 'system';
  entityId?: string;
  entityLabel?: string;
  action: string;
  oldValue?: unknown;
  newValue?: unknown;
  notes?: string;
  source?: string;
}

// ---------- Flags ----------

export type TaskFlag =
  | 'overdue'
  | 'no_next_action'
  | 'no_client'
  | 'no_estimate'
  | 'over_estimate'
  | 'stale_update'
  | 'ready_to_bill'
  | 'waiting_on_client'
  | 'needs_recap';

export interface TaskFlagDescriptor {
  flag: TaskFlag;
  label: string;
  /** Tone for badge styling. */
  tone: 'red' | 'amber' | 'pink' | 'cyan' | 'purple' | 'green' | 'neutral';
  description: string;
}

export const TASK_FLAG_DESCRIPTORS: Record<TaskFlag, TaskFlagDescriptor> = {
  overdue: {
    flag: 'overdue',
    label: 'Overdue',
    tone: 'red',
    description: 'Target date has passed and the task is not closed.',
  },
  no_next_action: {
    flag: 'no_next_action',
    label: 'No next action',
    tone: 'amber',
    description: 'The "Next Action" field is empty.',
  },
  no_client: {
    flag: 'no_client',
    label: 'No client',
    tone: 'amber',
    description: 'No linked client. Likely needs triage.',
  },
  no_estimate: {
    flag: 'no_estimate',
    label: 'No estimate',
    tone: 'amber',
    description: 'No time estimate set.',
  },
  over_estimate: {
    flag: 'over_estimate',
    label: 'Over estimate',
    tone: 'red',
    description: 'Tracked time exceeds the estimate.',
  },
  stale_update: {
    flag: 'stale_update',
    label: 'Stale',
    tone: 'amber',
    description: 'No status update in the last 7 days.',
  },
  ready_to_bill: {
    flag: 'ready_to_bill',
    label: 'Ready to bill',
    tone: 'green',
    description: 'Billable work with tracked time and unbilled status.',
  },
  waiting_on_client: {
    flag: 'waiting_on_client',
    label: 'Waiting on client',
    tone: 'cyan',
    description: 'Status indicates the client owns the next move.',
  },
  needs_recap: {
    flag: 'needs_recap',
    label: 'Needs recap',
    tone: 'pink',
    description: 'Closing the task without a recap added.',
  },
};

// ---------- API response envelopes ----------

export interface PaginatedResponse<T> {
  items: T[];
  /** Opaque cursor for the next page; null/undefined if no more. */
  nextCursor?: string | null;
  /** Total count if known (Airtable does not provide this for free). */
  total?: number;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

// ---------- Quick Capture / Close Task ----------

export type QuickCaptureSource = 'inbox' | 'client_email' | 'phone' | 'text' | 'internal' | 'idea';
export type QuickCaptureType = 'support' | 'project' | 'billing' | 'sales' | 'internal' | 'product';
export type QuickCaptureTarget = 'task' | 'support_ticket' | 'lead' | 'internal_backlog';

export interface QuickCaptureInput {
  title: string;
  source: QuickCaptureSource;
  clientId?: string;
  type: QuickCaptureType;
  priority?: SemanticPriority;
  dueDate?: string;
  description?: string;
  billable: boolean;
  createAs: QuickCaptureTarget;
}

export interface CloseTaskChecklist {
  workCompleted: boolean;
  qaChecked: boolean;
  timeLogged: boolean;
  recapAdded: boolean;
  clientUpdateSent: boolean;
  clientUpdateNotNeeded: boolean;
  billingReviewed: boolean;
  followUpCreated: boolean;
}

export interface CloseTaskInput {
  taskId: string;
  checklist: CloseTaskChecklist;
  recap?: string;
  followUpTitle?: string;
}
